import { useEffect } from "react";
import { useImmer } from "use-immer";
import { GET_COURSE_TASK_LIST, updateCourseTaskStatus } from "..";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCourseTaskListQuery,
  useCourseTaskDetailsQuery,
  useStudentSpecificDataQuery,
} from ".";
import { Axios, errorFormatter, getCookie, getSelectedProgram } from "utils";
import { useModal } from "shared";
import { v4 as uuidv4 } from "uuid";

export const useCourseTask = ({ load = false, courseId = "" }) => {
  const queryClient = useQueryClient();
  const { modalState, onOpenModal, onCloseModal } = useModal();
  const [courseTaskDetails, setCourseTaskDetails] = useImmer(null);

  let selectedProgram = getSelectedProgram();
  let {
    name: selectedProgramName,
    id: programId,
    studentBatchId,
  } = !!selectedProgram && selectedProgram;

  let selectedCourseGroup = sessionStorage.getItem("courseGroup")
    ? JSON.parse(sessionStorage.getItem("courseGroup"))
    : null;
  let { name: courseGroupName } = !!selectedCourseGroup && selectedCourseGroup;

  let courseListStorage = sessionStorage.getItem("courseList")
    ? JSON.parse(sessionStorage.getItem("courseList"))
    : null;
  let { hasCourseGroup, displayName, courseType } =
    !!courseListStorage && courseListStorage;

  const localData = JSON.parse(getCookie("_stu_user_data"));
  const { refreshToken } = localData;

  const localStorageTask = sessionStorage.getItem("currentTask")
    ? JSON.parse(sessionStorage.getItem("currentTask"))
    : null;
  const [currentCourseTask, setCurrentCourseTask] = useImmer(
    localStorageTask || null
  );
  const [currentCourseTaskIndex, setCurrentCourseTaskIndex] = useImmer(null);

  const courseTaskList = useCourseTaskListQuery({
    load,
    programId,
    courseId,
    studentBatchId,
  });
  const { data } = courseTaskList;
  const { name, selectedstudentTasks, studentDataDisplayName, courseSections } =
    !!data && data;
  const [studentTasks, setStudentTasks] = useImmer(selectedstudentTasks || []);
  const [studentTheory, setStudentTheory] = useImmer(courseSections || []);
  const [studentTheoryDetails, setStudentTheoryDetails] = useImmer({
    name: "",
    description: "",
    studentDataDisplayName: "",
  });

  const studentTaskId = currentCourseTask?.id;
  const courseTaskId = currentCourseTask?.courseTaskId;

  const courseTaskDetailsQuery = useCourseTaskDetailsQuery({
    load: load && !!currentCourseTask?.courseTaskId,
    courseTaskId,
    studentTaskId,
  });

  const studentSpecificDataQuery = useStudentSpecificDataQuery({
    load:
      modalState?.isOpen && modalState?.actionFrom === "student_specific_data",
    studentBatchId,
    courseId,
  });

  const onCourseTaskCardClick = (currentTask) => {
    setCurrentCourseTask(currentTask);
    let newCurrentIndex = studentTasks?.findIndex(
      (e) => e?.courseTaskId === currentTask?.courseTaskId
    );
    setCurrentCourseTaskIndex(newCurrentIndex);
    sessionStorage.setItem("currentTask", JSON.stringify(currentTask));
  };
  useEffect(() => {
    if (courseTaskDetailsQuery?.data) {
      setCourseTaskDetails((draft) => {
        draft = {
          ...courseTaskDetailsQuery.data,
          taskContents: courseTaskDetailsQuery.data.taskContents.map(
            (item) => ({
              ...item,
              key: uuidv4(),
            })
          ),
          taskSections: courseTaskDetailsQuery.data.taskSections.map(
            (item) => ({
              ...item,
              key: uuidv4(),
              sectionContents: item?.sectionContents?.map((subItem) => ({
                ...subItem,
                key: uuidv4(),
              })),
            })
          ),
        };
        return draft;
      });
    }
  }, [courseTaskDetailsQuery?.data]);

  useEffect(() => {
    if (courseTaskList?.data?.selectedstudentTasks) {
      setStudentTasks(courseTaskList?.data?.selectedstudentTasks);
    }
    if (courseTaskList?.data?.courseSections) {
      setStudentTheory(courseTaskList?.data?.courseSections);
      setStudentTheoryDetails((draft) => {
        draft.name = courseTaskList?.data?.name;
        draft.description = courseTaskList?.data?.description;
        draft.studentDataDisplayName =
          courseTaskList?.data?.studentDataDisplayName;
        return draft;
      });
    }
  }, [courseTaskList?.data]);

  const updateCourseTasksStatus = useMutation({
    mutationFn: updateCourseTaskStatus,
    onSuccess: () => {
      queryClient.invalidateQueries([
        GET_COURSE_TASK_LIST,
        programId,
        courseId,
        studentBatchId,
      ]);
    },
    onError: (e) => {
      errorFormatter(e);
    },
  });

  const onStartSimulation = async (isTrading = false) => {
    const newWindow = window.open("/starting-simulation", "_blank");
    if (!newWindow) {
      alert("Pop-up blocked. Please allow pop-ups for this site.");
      return;
    }

    try {
      const { id } = currentCourseTask;
      const res = await Axios.get(
        `v2/portal/CourseTasks/${id}/simulation-token/?refreshToken=${refreshToken}`
      );
      if (res.status === 200) {
        if (isTrading) {
          let environment = "dev.";
          if (
            process.env.REACT_APP_ENVIORNMENT_KEY === "preprod" ||
            process.env.REACT_APP_ENVIORNMENT_KEY === "qa"
          ) {
            environment = "qa.";
          } else if (process.env.REACT_APP_ENVIORNMENT_KEY === "production") {
            environment = "";
          }
          newWindow.location.href = `https://npractice.${environment}nergyindia.com/trading-tool/?taskId=${id}&token=${res.data}&callback=${process.env.REACT_APP_BASE_URL}`;
        } else {
          const separator = courseTaskDetailsQuery?.data?.endPoint.includes("?")
            ? "&"
            : "?";
          newWindow.location.href = `${courseTaskDetailsQuery?.data?.endPoint}${separator}taskId=${id}&token=${res.data}&callback=${process.env.REACT_APP_BASE_URL}`;
        }
      } else {
        throw Error;
      }
    } catch (err) {
      if (err.response.status === 403) {
        onOpenModal({ actionFrom: "forbidden" });
      }
      newWindow.close();
    }
  };

  const onPrevTask = () => {
    if (currentCourseTaskIndex) {
      onCourseTaskCardClick(studentTasks[currentCourseTaskIndex - 1]);
    }
  };

  const onNextTask = () => {
    if (currentCourseTaskIndex || currentCourseTaskIndex === 0) {
      onCourseTaskCardClick(studentTasks[currentCourseTaskIndex + 1]);
    }
  };

  return {
    name,
    studentTasks,
    courseTaskList,
    hasCourseGroup,
    displayName,
    selectedProgramName,
    courseGroupName,
    onCourseTaskCardClick,
    courseTaskDetailsQuery,
    courseType,
    currentCourseTask,
    updateCourseTasksStatus,
    onStartSimulation,
    setCurrentCourseTask,
    studentDataDisplayName,
    modalState,
    onOpenModal,
    onCloseModal,
    studentSpecificDataQuery,
    studentTheory,
    currentCourseTaskIndex,
    setCurrentCourseTaskIndex,
    onPrevTask,
    onNextTask,
    studentTheoryDetails,
    courseTaskDetails,
  };
};
